import { EditorEvents } from "@tiptap/react";
import { useEffect, useRef } from "react";
import { DebounceProps } from "../field/field";
import {
  Editor,
  EditorBoldItalicUnderlineGroup,
  EditorBulletList,
  EditorContent,
  editorGenerateHTML,
  EditorOrderedList,
  editorParseHTML,
  EditorToolbar,
  EditorToolbarSeparator,
  useEditor,
} from "./editor";
import { EditorProps } from "@tiptap/pm/view";
import {
  EditorVariable,
  EditorVariableNode,
  EditorVariables,
  textToVariableNodes,
} from "./editor-variables";
import { EditorVariableDropdown } from "./editor-variable-dropdown";

export type EditorInputProps = {
  initialHtml: string;
  onHtmlChange: (html: string) => void;
  editorProps?: EditorProps;
  disabled?: boolean;
  /** Marks the editor as invalid (`aria-invalid`), e.g. on a validation error. */
  invalid?: boolean;
  /**
   * Variables of the text: they are shown with their label and can be
   * inserted from the toolbar. Without them, neither is offered.
   */
  variables?: EditorVariable[];
} & DebounceProps;

/**
 * EditorInput is a convenience abstraction to cover the common use
 * case of the Editor being used to handle a special text field.
 * It contains common editing options and a title and description
 * to explain the purpose of the field.
 * It expects an initial HTML value and returns updated HTML through
 * the onHtmlChange callback.
 *
 * With `debounce`, changes are reported once the user pauses typing,
 * like {@link InputDebounced}. It defaults to false here;
 * {@link EditorInputField} turns it on, like every other field component.
 *
 * A pending change is flushed on blur and on unmount, so an edit is not
 * lost when the editor goes away. Note that the unmount flush can only
 * reach the parent; whether it still reaches the server depends on the
 * parent's save mechanism outliving this component (it does not when the
 * whole page unmounts without a blur, e.g. on a programmatic navigation).
 */
export function EditorInput({
  initialHtml,
  onHtmlChange,
  disabled = false,
  invalid = false,
  editorProps,
  variables,
  debounce = false,
  delay = 300,
}: EditorInputProps) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The html not yet reported to the caller, null when nothing is pending.
  const pending = useRef<string | null>(null);
  // Mirrored so `flush` reads the current handler from a timer, from blur
  // and from unmount.
  const onHtmlChangeRef = useRef(onHtmlChange);
  onHtmlChangeRef.current = onHtmlChange;

  const flush = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (pending.current === null) {
      return;
    }
    const html = pending.current;
    pending.current = null;
    onHtmlChangeRef.current(html);
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  const onUpdate = (event: EditorEvents["update"]) => {
    if (disabled) return;
    pending.current = editorGenerateHTML(event.editor);
    if (!debounce) {
      // Through `flush`, so a timer still pending from before `debounce`
      // was turned off is cancelled and cannot report older html later.
      flush();
      return;
    }
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => flushRef.current(), delay);
  };
  const onBlur = () => flushRef.current();

  const extensions = variables
    ? [
        EditorVariables.configure({ variables }),
        EditorVariableNode.configure({ variables }),
      ]
    : [];
  // The variables of the loaded text are shown as chips. This is a load,
  // not an edit, so it reports no change.
  const content = variables
    ? textToVariableNodes(
        editorParseHTML(initialHtml, extensions),
        variables.map((v) => v.name),
      )
    : initialHtml;

  const editor = useEditor({
    content,
    extensions,
    onUpdate,
    onBlur,
    editable: !disabled,
    editorProps: {
      ...editorProps,
      attributes: {
        ...editorProps?.attributes,
        ...(invalid && { "aria-invalid": "true" }),
      },
    },
  });

  useEffect(() => {
    // `false`: toggling editability is not an edit. Emitting an update
    // here would report the (normalised) initial html as a change and
    // trigger a save on every mount.
    editor.setEditable(!disabled, false);
  }, [disabled, editor]);

  // Do not drop a pending change when the editor goes away. `flush` only
  // touches refs, so reading it through one keeps this effect free of
  // dependencies.
  useEffect(() => () => flushRef.current(), []);

  return (
    <Editor editor={editor}>
      <EditorToolbar>
        <EditorBoldItalicUnderlineGroup />
        <EditorToolbarSeparator />
        <EditorBulletList />
        <EditorOrderedList />
        {variables && (
          <>
            <EditorToolbarSeparator />
            <EditorVariableDropdown variables={variables} />
          </>
        )}
      </EditorToolbar>
      <EditorContent />
    </Editor>
  );
}
